import { Directive, ElementRef, input, Input, OnChanges, OnDestroy, output, SimpleChanges } from '@angular/core';
import { Alignment, Fit, Layout, Rive, RiveParameters } from '@rive-app/canvas';

export type RiveAnimationConfig = Omit<RiveParameters, 'canvas' | 'src' | 'onLoad'>;

@Directive({
  selector: 'canvas[sbRiveAnimation]',
  exportAs: 'sb-rive-animation',
  standalone: true,
})
export class RiveAnimationDirective implements OnChanges, OnDestroy {
  @Input({ required: true, alias: 'sbRiveAnimation' })
  public path!: string;

  public loadCompleted = output<void>();

  public loading = true;

  private riveAnimation?: Rive;

  public constructor(private el: ElementRef) {}

  public sbRiveAnimationConfig = input<RiveAnimationConfig>({ autoplay: true });

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['path'] && this.path) {
      this.riveAnimation = new Rive({
        src: this.path,
        canvas: this.el.nativeElement,
        autoplay: true,
        layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        onLoad: () => {
          this.riveAnimation?.resizeDrawingSurfaceToCanvas();
          this.loading = false;
          this.loadCompleted.emit();
        },
        onLoadError: () => {
          console.error(`Failed to load Rive animation from path: ${this.path}`);
          this.loading = false;
        },
        ...this.sbRiveAnimationConfig(),
      });
    }
  }

  public play() {
    this.riveAnimation?.play();
  }

  public reset(autoplay?: boolean) {
    this.loading = true;
    // (Re)loading the animation is more reliable than calling this.riveAnimation.reset()
    this.riveAnimation?.load({
      src: this.path,
      autoplay: autoplay ?? this.sbRiveAnimationConfig().autoplay,
    });
  }

  public stop() {
    this.reset(false);
  }

  public ngOnDestroy() {
    this.riveAnimation?.cleanup();
  }
}
